import type { Class } from '../../types/tournament';
import type {
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleConstraints,
  IScheduleItem,
  ITournamentSchedule,
} from '../../types/schedule';
import { isFreeSlot } from '../../types/schedule';
import { getPlacementConflictIndex } from './placementConflictIndexCache';
import {
  canPlaceMatchWithoutConflict,
  type PlacementConflictIndex,
} from './schedulePlacementConflict';
import { isSwapTargetBlockedByConflict } from '../../pages/tourDetails/Schedule/components/DnDSchedule/scheduleGridSwapPreview';

export type DragDropCommitParams = {
  sourceMatch: IMatchScheduleItem;
  sourceIsScheduled: boolean;
  targetData: IScheduleItem | IFreeSlotItem;
  schedule: ITournamentSchedule;
  constraints: IScheduleConstraints;
  timeZone: string;
  classIdToObject: Record<string, Class>;
  blockInvalidPlacement: boolean;
  allScheduledMatchesFlat: IMatchScheduleItem[];
  dragPlacementSource: 'scheduled' | 'unscheduled' | null;
  unscheduledMatches?: IMatchScheduleItem[];
  conflictIndex?: PlacementConflictIndex | null;
};

export type ClickPlacementCommitParams = {
  freeSlot: IFreeSlotItem;
  match: IMatchScheduleItem;
  clickSource: 'unscheduled' | 'scheduled_grid';
  schedule: ITournamentSchedule;
  constraints: IScheduleConstraints;
  timeZone: string;
  classIdToObject: Record<string, Class>;
  blockInvalidPlacement: boolean;
  conflictIndex?: PlacementConflictIndex | null;
};

export function canCommitClickPlacement(params: ClickPlacementCommitParams): boolean {
  if (!params.blockInvalidPlacement) return true;

  const startMs = new Date(params.freeSlot.scheduleDate).getTime();
  if (!Number.isFinite(startMs)) return false;

  const conflictIndex =
    params.conflictIndex ??
    getPlacementConflictIndex(
      params.schedule,
      params.constraints,
      params.timeZone,
      null,
      [],
      params.classIdToObject
    );

  return canPlaceMatchWithoutConflict({
    pending: params.match,
    courtId: params.freeSlot.court.id,
    startMs,
    schedule: params.schedule,
    constraints: params.constraints,
    timeZone: params.timeZone,
    classIdToObject: params.classIdToObject,
    excludeMatchIds: params.clickSource === 'scheduled_grid' ? [params.match.id] : [],
    conflictIndex,
  });
}

export function canCommitDragDrop(params: DragDropCommitParams): boolean {
  if (!params.blockInvalidPlacement) return true;

  const {
    sourceMatch,
    sourceIsScheduled,
    targetData,
    schedule,
    constraints,
    timeZone,
    classIdToObject,
    allScheduledMatchesFlat,
    dragPlacementSource,
    unscheduledMatches = [],
  } = params;

  if (isFreeSlot(targetData)) {
    const startMs = new Date(targetData.scheduleDate).getTime();
    if (!Number.isFinite(startMs)) return false;
    const conflictIndex =
      params.conflictIndex ??
      getPlacementConflictIndex(
        schedule,
        constraints,
        timeZone,
        null,
        unscheduledMatches,
        classIdToObject
      );
    return canPlaceMatchWithoutConflict({
      pending: sourceMatch,
      courtId: targetData.court.id,
      startMs,
      schedule,
      constraints,
      timeZone,
      classIdToObject,
      excludeMatchIds: sourceIsScheduled ? [sourceMatch.id] : [],
      conflictIndex,
      lookupMatches: unscheduledMatches,
    });
  }

  const targetMatch = targetData as IMatchScheduleItem;
  if (targetMatch.id === sourceMatch.id) return false;

  const swapConflictIndex =
    params.conflictIndex ??
    getPlacementConflictIndex(
      schedule,
      constraints,
      timeZone,
      null,
      unscheduledMatches,
      classIdToObject
    );

  return !isSwapTargetBlockedByConflict({
    activePlacementMatch: sourceMatch,
    scheduleConstraints: constraints,
    rangeItem: targetMatch,
    allScheduledMatchesFlat,
    pendingPlacementSource: sourceIsScheduled ? 'scheduled_grid' : 'unscheduled',
    scheduleDragActive: true,
    dragPlacementSource,
    blockInvalidPlacement: true,
    canonicalSchedule: schedule,
    scheduleTimeZone: timeZone,
    classIdToObject,
    swapConflictIndex,
  });
}
